/**
 * Sample JSON payloads for engine demos & chat embedding.
 * Pass as: { type: 'miniapp', app: 'engine_multiple_choice', props: { title, content, hints } }
 */

export const sampleAnimalHabitatsActivity = {
  title: 'Animal homes',
  hints: [
    'Think about what animals need to stay alive every day.',
    'Oceans and forests can both be habitats for different creatures.',
  ],
  content: {
    instructions: 'Pick the answer that fits best. Tap your choice.',
    questions: [
      {
        id: 'q1',
        prompt: 'What does a habitat give animals?',
        choices: [
          { id: 'a', text: 'Only toys', correct: false },
          { id: 'b', text: 'Food, water, and shelter', correct: true },
          { id: 'c', text: 'Nothing', correct: false },
        ],
        explanation: 'A good habitat helps animals find food, water, space, and shelter.',
      },
      {
        id: 'q2',
        prompt: 'A sandy habitat with lots of cactus might be —',
        choices: [
          { id: 'a', text: 'A rainy jungle', correct: false },
          { id: 'b', text: 'A desert', correct: true },
          { id: 'c', text: 'The deep ocean', correct: false },
        ],
        explanation: 'Cacti and sand are clues for a dry desert habitat.',
      },
    ],
  },
};

export const sampleRobotMovementSequence = {
  title: 'Robot safe start',
  hints: [
    'What should happen before the robot moves?',
    'Small slow tests come after the area is clear.',
  ],
  content: {
    instructions: 'Use the arrows to put the steps in a safe order.',
    items: [
      { id: 's1', text: 'Clear the floor of people and pets.' },
      { id: 's2', text: 'Turn on power after an adult checks.' },
      { id: 's3', text: 'Let sensors warm up and calibrate.' },
      { id: 's4', text: 'Try one small slow movement first.' },
    ],
    correctOrder: ['s1', 's2', 's3', 's4'],
  },
};

export const sampleMathPatternActivity = {
  title: 'What comes next?',
  hints: [
    'Say the numbers out loud — do you add the same amount each time?',
    'The gap between numbers might be the key.',
  ],
  content: {
    instructions: 'Look at the pattern. Which number comes next?',
    sequence: [2, 4, 6, 8],
    choices: [
      { id: 'a', text: '9', correct: false },
      { id: 'b', text: '10', correct: true },
      { id: 'c', text: '7', correct: false },
    ],
    explanation: 'We add 2 each time, so after 8 comes 10.',
  },
};

/** Optional: match pairs for demo tray */
export const sampleMatchColors = {
  title: 'Match the word',
  hints: ['Read the label on the dotted box, then pick the matching chip.'],
  content: {
    instructions: 'Tap a chip, then tap the box it belongs in.',
    targets: [
      { id: 't1', label: 'Lives in water' },
      { id: 't2', label: 'Lives on land' },
    ],
    draggables: [
      { id: 'd1', text: 'Frog (pond)', matchesTargetId: 't1' },
      { id: 'd2', text: 'Rabbit (meadow)', matchesTargetId: 't2' },
    ],
  },
};

export const sampleCanvasHabitatsScene = {
  title: 'Drag each animal to its habitat',
  hints: [
    'Think about temperature, food, and water for each animal.',
    'Polar animals love cold; reef animals need salt water.',
  ],
  content: {
    instructions: 'Drag each animal onto the habitat where it belongs.',
    scene: {
      viewBox: '0 0 200 120',
      background: [
        { type: 'rect', x: 0, y: 0, width: 200, height: 60, fill: '#bee3f8' },
        { type: 'rect', x: 0, y: 60, width: 200, height: 60, fill: '#fef3c7' },
        { type: 'circle', cx: 30, cy: 18, r: 8, fill: '#fde68a', opacity: 0.85 },
      ],
      zones: [
        { id: 'ice', label: 'Ice & snow', x: 0, y: 0, width: 100, height: 60, fill: 'rgba(255,255,255,0.45)', labelPos: { x: 50, y: 12 } },
        { id: 'reef', label: 'Coral reef', x: 100, y: 0, width: 100, height: 60, fill: 'rgba(56, 189, 248, 0.35)', labelPos: { x: 150, y: 12 } },
        { id: 'savanna', label: 'Savanna', x: 0, y: 60, width: 100, height: 60, fill: 'rgba(202, 138, 4, 0.22)', labelPos: { x: 50, y: 72 } },
        { id: 'forest', label: 'Rainforest', x: 100, y: 60, width: 100, height: 60, fill: 'rgba(22, 163, 74, 0.32)', labelPos: { x: 150, y: 72 } },
      ],
    },
    pieces: [
      { id: 'p1', label: 'Polar bear', emoji: '🐻\u200d❄️', homeZoneId: 'ice' },
      { id: 'p2', label: 'Clownfish', emoji: '🐠', homeZoneId: 'reef' },
      { id: 'p3', label: 'Lion', emoji: '🦁', homeZoneId: 'savanna' },
      { id: 'p4', label: 'Tree frog', emoji: '🐸', homeZoneId: 'forest' },
    ],
    victory: { message: 'All animals are home!' },
  },
};

export const sampleTapOnImageReef = {
  title: 'Find the predators',
  hints: ['Predators usually have sharp teeth or spines.'],
  content: {
    instructions: 'Tap every animal that hunts other animals in this reef.',
    scene: {
      viewBox: '0 0 200 120',
      background: [
        { type: 'rect', x: 0, y: 0, width: 200, height: 120, fill: '#bae6fd' },
        { type: 'rect', x: 0, y: 100, width: 200, height: 20, fill: '#fde68a' },
      ],
      decorations: [
        { type: 'emoji', x: 100, y: 14, content: '☀️', fontSize: 10 },
      ],
    },
    hotspots: [
      { id: 'shark', label: 'Shark', cx: 50, cy: 60, r: 12, correct: true, emoji: '🦈', feedback: 'Sharks hunt fish — predator.' },
      { id: 'crab', label: 'Crab', cx: 30, cy: 100, r: 8, correct: false, emoji: '🦀', feedback: 'Crabs mostly scavenge — not the right answer here.' },
      { id: 'octopus', label: 'Octopus', cx: 150, cy: 80, r: 12, correct: true, emoji: '🐙', feedback: 'Octopuses hunt small prey — predator.' },
      { id: 'starfish', label: 'Starfish', cx: 95, cy: 100, r: 7, correct: false, emoji: '⭐', feedback: 'Starfish mostly eat algae here.' },
      { id: 'fish', label: 'Clownfish', cx: 170, cy: 50, r: 6, correct: false, emoji: '🐠', feedback: 'Clownfish eat plankton, not other fish.' },
    ],
    victory: { message: 'Sharp eyes!' },
  },
};

export const sampleSimulationRobotPath = {
  title: 'Stop the robot at the flag',
  hints: ['Watch when the robot lines up under the flag.'],
  content: {
    instructions: 'Play the simulation, then drag the slider so the robot is exactly at the flag and tap "Mark this moment".',
    scene: {
      viewBox: '0 0 200 120',
      background: [
        { type: 'rect', x: 0, y: 0, width: 200, height: 80, fill: '#e0f7fa' },
        { type: 'rect', x: 0, y: 80, width: 200, height: 40, fill: '#c8e6c9' },
        { type: 'path', d: 'M0 80 L200 80', stroke: '#94a3b8', strokeWidth: 0.6, fill: 'none' },
      ],
    },
    totalFrames: 60,
    sprites: [
      {
        id: 'robot',
        type: 'emoji',
        content: '🤖',
        fontSize: 18,
        keyframes: [
          { frame: 0, x: 12, y: 70 },
          { frame: 60, x: 180, y: 70 },
        ],
      },
      {
        id: 'flag',
        type: 'emoji',
        content: '🚩',
        fontSize: 18,
        keyframes: [{ frame: 0, x: 150, y: 70 }],
      },
    ],
    task: { type: 'stop_at_frame', correctFrame: 46, tolerance: 4, promptHint: 'Stop the playback when the robot is right under the flag.' },
    victory: { message: 'Right on the flag!' },
  },
};

export const sampleBranchingMcAnimals = {
  title: 'Habitat reasoning',
  hints: ['Look at body features — fur, scales, gills.'],
  content: {
    instructions: 'Pick the best answer. If you slip up, a quick check will help you reason it out.',
    questions: [
      {
        id: 'q1',
        prompt: 'A frog has thin moist skin and likes wet places. Which habitat fits?',
        choices: [
          { id: 'a', text: 'Hot dry desert', correct: false },
          { id: 'b', text: 'Damp pond and forest floor', correct: true },
          { id: 'c', text: 'Open icy tundra', correct: false },
        ],
        explanation: 'Frogs need moisture to keep their skin healthy.',
        followUp: {
          prompt: 'Which one of these does a frog need most?',
          hint: 'Think about what its skin must touch often to stay healthy.',
          choices: [
            { id: 'fa', text: 'Dry sand', correct: false, explanation: 'Sand pulls moisture out of skin.' },
            { id: 'fb', text: 'Moisture and shade', correct: true, explanation: 'Yes — that points to a damp habitat.' },
            { id: 'fc', text: 'Constant snow', correct: false, explanation: 'Frogs cannot stay frozen for long.' },
          ],
        },
      },
    ],
  },
};

export const sampleGearRatioConveyor = {
  title: 'Slow the conveyor',
  hints: [
    'Output rpm = motor rpm × (motor teeth ÷ driven teeth).',
    'A larger driven gear means slower output. A smaller driven gear means faster output.',
  ],
  content: {
    instructions:
      'The motor spins at 60 rpm with 24 teeth. Pick a driven gear to make the conveyor run at 30 rpm.',
    driver: { teeth: 24, rpm: 60, label: 'Motor' },
    gears: [
      { id: 'g12', teeth: 12, label: 'Small' },
      { id: 'g24', teeth: 24, label: 'Same' },
      { id: 'g36', teeth: 36, label: 'Big' },
      { id: 'g48', teeth: 48, label: 'Big-big' },
    ],
    target: { rpm: 30, tolerance: 1 },
    victory: { message: 'Locked in at 30 rpm — the conveyor is moving safely.' },
  },
};

export const sampleCircuitBuilderLed = {
  title: 'Wire up the LED',
  hints: [
    'Tap two terminals to draw a wire between them. Tap a wire to remove it.',
    'Path: battery + → switch → resistor → LED anode (top), and LED cathode (bottom) → battery −.',
    'Don\'t forget to close the switch — tap the lever or the toggle button below.',
  ],
  content: {
    instructions:
      'Build a path from battery + through the switch and resistor into the LED, then back to battery −. Close the switch and the LED should glow.',
    scene: { viewBox: '0 0 340 220' },
    components: [
      { id: 'bat', kind: 'battery', x: 50, y: 110, label: '9V' },
      { id: 'sw', kind: 'switch', x: 130, y: 50, label: 'SW1', closed: false },
      { id: 'r1', kind: 'resistor', x: 220, y: 50, label: '330Ω' },
      { id: 'led', kind: 'led', x: 290, y: 110, label: 'LED', color: '#ef4444' },
    ],
    wires: [],
    goal: {
      type: 'light_led',
      batteryId: 'bat',
      ledId: 'led',
      requireSwitchClosed: 'sw',
      requireResistorInPath: 'r1',
    },
    victory: { message: 'You closed the loop — the LED is glowing!' },
  },
};

export const sampleGearDirectionTrain = {
  title: 'Gear direction detective (demo)',
  hints: ['Each mesh flips spin direction.', 'Same-spin gears are two hops apart on a line.'],
  content: {
    instructions: 'Start is the reference only — tap other gears that match its spin, then Check.',
    prompt: 'Tap every other gear that spins the same way as Start.',
    starterGearId: 'g1',
    targetRule: 'same_direction_as_starter',
    scene: { width: 320, height: 190 },
    gears: [
      { id: 'g1', x: 55, y: 100, radius: 32, teeth: 22, direction: 1, label: 'Start' },
      { id: 'g2', x: 130, y: 100, radius: 28, teeth: 18, label: 'B' },
      { id: 'g3', x: 205, y: 100, radius: 28, teeth: 18, label: 'C' },
      { id: 'g4', x: 275, y: 100, radius: 26, teeth: 16, label: 'D' },
    ],
    connections: [{ from: 'g1', to: 'g2' }, { from: 'g2', to: 'g3' }, { from: 'g3', to: 'g4' }],
    victory: { message: 'Spin squad found!' },
  },
};

export const sampleGearSpeedFastest = {
  title: 'Gear speed race (demo)',
  hints: ['Smaller teeth on the driven side usually spin faster.', 'Watch the blur.'],
  content: {
    instructions: 'Tap the fastest gear, then Check.',
    prompt: 'Which gear spins the fastest?',
    questionMode: 'fastest',
    answer: 'g3',
    referenceGearId: 'g1',
    scene: { width: 320, height: 190 },
    gears: [
      { id: 'g1', x: 70, y: 100, radius: 38, teeth: 36, label: 'Driver' },
      { id: 'g2', x: 160, y: 100, radius: 28, teeth: 18, label: 'Mid' },
      { id: 'g3', x: 250, y: 100, radius: 22, teeth: 12, label: 'Tiny' },
    ],
    connections: [{ from: 'g1', to: 'g2' }, { from: 'g2', to: 'g3' }],
    victory: { message: 'Fastest gear!' },
  },
};

export const sampleCircuitPathRow = {
  title: 'Circuit path builder (demo)',
  hints: ['Tap gaps until each shows wire.', 'Then tap Check.'],
  content: {
    instructions: 'Fill every gap with wire so the bulb can glow.',
    prompt: 'Tap each gap until it shows wire, then Check.',
    pieceLabels: {
      empty: { label: 'Open', icon: '○' },
      wire: { label: 'Wire', icon: '〰️' },
    },
    slots: [
      { id: 'b1', role: 'battery', label: 'Battery' },
      { id: 'g1', role: 'gap', options: ['empty', 'wire'], correct: 'wire' },
      { id: 'l1', role: 'bulb', label: 'Bulb' },
      { id: 'g2', role: 'gap', options: ['empty', 'wire'], correct: 'wire' },
      { id: 'gnd', role: 'label', icon: '⏚', label: '−' },
    ],
    victory: { message: 'Glow!' },
  },
};

export const sampleSwitchLogicAnd = {
  title: 'Switch logic (demo)',
  hints: ['AND needs every switch ON for the preview glow.', 'Check wants the exact goal pattern.'],
  content: {
    instructions: 'Set both switches ON.',
    question: 'Turn A and B ON under the AND rule.',
    switches: [
      { id: 'a', label: 'Switch A' },
      { id: 'b', label: 'Switch B' },
    ],
    logic: { type: 'AND' },
    output: { type: 'bulb' },
    correctState: { a: true, b: true },
    victory: { message: 'Logic matched!' },
  },
};

export const sampleExplorationCards = {
  title: 'Kelp forest tour',
  hints: ['Read each card — think: who eats whom?'],
  content: {
    cards: [
      {
        id: 'c1',
        title: 'What is kelp?',
        body: 'Kelp is a giant seaweed that grows toward sunlight. Fish hide between the fronds.',
        followUpQuestion: 'Why does kelp need sunlight?',
      },
      {
        id: 'c2',
        title: 'Sea otters',
        body: 'Sea otters eat sea urchins. Too many urchins can munch kelp down to stubs.',
        followUpQuestion: 'What might happen if otters disappear?',
      },
    ],
  },
};
